import { useState, FormEvent } from 'react';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setLoading(true);
    const formData = new URLSearchParams();
    const fd = new FormData(form);
    fd.forEach((value, key) => formData.append(key, value.toString()));

    try {
      await fetch('https://readdy.ai/api/form/d728beohb0rqicn68u50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
      form.reset();
      setCharCount(0);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-28 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-stretch">
          {/* Left: Image & Info */}
          <div className="relative rounded-2xl overflow-hidden min-h-[600px]">
            <img
              src="https://public.readdy.ai/ai/img_res/e9d27987fbf9fff7602fa504486f0184.jpg"
              alt="Técnico de Segurança RE_CONECTA"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/95 via-neutral-950/50 to-neutral-950/10"></div>

            {/* Overlay Content */}
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="w-12 h-12 flex items-center justify-center mb-4">
                <i className="ri-headphone-line text-4xl text-red-500"></i>
              </div>
              <h3 className="text-white font-bold text-2xl mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Pronto para Proteger<br />seu Negócio?
              </h3>
              <p className="text-neutral-300 text-sm leading-relaxed mb-7">
                Fale com nossos especialistas e descubra a solução ideal para a segurança da sua empresa.
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-red-600/20 border border-red-600/30 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-red-400 text-sm"></i>
                  </div>
                  <span className="text-neutral-300 text-sm">re-conecta@re-conectatecnologia.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-red-600/20 border border-red-600/30 rounded-lg flex-shrink-0">
                    <i className="ri-whatsapp-line text-red-400 text-sm"></i>
                  </div>
                  <span className="text-neutral-300 text-sm">54-997056430</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-red-600/20 border border-red-600/30 rounded-lg flex-shrink-0">
                    <i className="ri-instagram-line text-red-400 text-sm"></i>
                  </div>
                  <a
                    href="https://www.instagram.com/re_conectatecnologia?igsh=ejY5cjQzc3oxeTBs&utm_source=qr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-neutral-300 text-sm hover:text-red-400 transition-colors cursor-pointer"
                  >
                    @re_conectatecnologia
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-red-600/20 border border-red-600/30 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-red-400 text-sm"></i>
                  </div>
                  <span className="text-neutral-300 text-sm">Suporte 24/7 disponível</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-red-600/20 border border-red-600/30 rounded-lg flex-shrink-0">
                    <i className="ri-tools-line text-red-400 text-sm"></i>
                  </div>
                  <a
                    href="https://reconecta.auvo.com.br/Login#signin"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-neutral-300 text-sm hover:text-red-400 transition-colors cursor-pointer"
                  >
                    Abrir chamado de manutenção / orçamento
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl border border-neutral-200 p-10">
            <span className="inline-block text-red-600 text-xs font-bold uppercase tracking-widest bg-red-50 px-3 py-1.5 rounded-full mb-5">
              / Contato
            </span>
            <h2 className="text-3xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Fale com a{' '}
              <span className="text-red-600">RE_CONECTA</span>
            </h2>
            <p className="text-neutral-500 text-sm mb-6 leading-relaxed">
              Preencha o formulário abaixo e nossa equipe técnica entrará em contato em breve.
            </p>

            {/* Chamados CTA */}
            <a
              href="https://reconecta.auvo.com.br/Login#signin"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 w-full bg-neutral-950 hover:bg-neutral-800 text-white px-5 py-3.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer mb-6 group"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-red-600 rounded-lg flex-shrink-0">
                <i className="ri-tools-line text-white text-base"></i>
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-semibold text-sm leading-none mb-0.5">Portal de Chamados</p>
                <p className="text-neutral-400 text-xs font-normal">Manutenção, suporte técnico e orçamentos</p>
              </div>
              <i className="ri-external-link-line text-neutral-400 group-hover:text-red-400 transition-colors"></i>
            </a>

            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-10 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-double-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-neutral-900 font-bold text-xl mb-2">Mensagem Enviada!</h3>
                <p className="text-neutral-500 text-sm leading-relaxed mb-5">
                  Obrigado pelo contato. Nossa equipe técnica retornará em breve.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-red-600 hover:text-red-700 text-sm font-semibold hover:underline cursor-pointer"
                >
                  Enviar nova mensagem
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="reconecta-contact-form"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-neutral-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Seu nome completo"
                      className="w-full border border-neutral-200 rounded-lg px-4 py-3 text-sm text-neutral-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-neutral-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      Empresa
                    </label>
                    <input
                      type="text"
                      name="company"
                      placeholder="Nome da empresa"
                      className="w-full border border-neutral-200 rounded-lg px-4 py-3 text-sm text-neutral-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-neutral-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    E-mail Corporativo *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="seu@empresa.com.br"
                    className="w-full border border-neutral-200 rounded-lg px-4 py-3 text-sm text-neutral-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-neutral-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="(xx) xxxxx-xxxx"
                    className="w-full border border-neutral-200 rounded-lg px-4 py-3 text-sm text-neutral-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-neutral-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    Tipo de Serviço
                  </label>
                  <select
                    name="service_type"
                    className="w-full border border-neutral-200 rounded-lg px-4 py-3 text-sm text-neutral-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all bg-white cursor-pointer"
                  >
                    <option value="">Selecione um serviço</option>
                    <option value="cftv">CFTV & Monitoramento</option>
                    <option value="access">Controle de Acesso</option>
                    <option value="alarm">Sistema de Alarme</option>
                    <option value="network">Redes & Infraestrutura</option>
                    <option value="maintenance">Manutenção Preventiva</option>
                    <option value="integration">Integração de Sistemas</option>
                    <option value="other">Outro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-neutral-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    Mensagem{' '}
                    <span className={`font-normal ml-1 ${charCount > 500 ? 'text-red-500' : 'text-neutral-400'}`}>
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Descreva sua necessidade de segurança..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-neutral-200 rounded-lg px-4 py-3 text-sm text-neutral-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || charCount > 500}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white py-3.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Enviar Solicitação
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
